// Prepare a photo in the browser before upload: decode (EXIF orientation applied by the
// browser), resize, and re-encode as JPEG. Re-encoding drops all metadata (EXIF, GPS, XMP).
// The server still validates type, size and dimensions and strips metadata itself.
import type { PhotoKind } from '../domain/types';

export const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';
const ACCEPTED = PHOTO_ACCEPT.split(',');
const MAX_INPUT_BYTES = 25 * 1024 * 1024;
const MAX_OUTPUT_BYTES = 1_900_000;
/** Longest side after resizing; avatars are centre-cropped to a square. */
const TARGET: Record<PhotoKind, number> = { avatar: 512, cover: 1600, background: 1600 };

export class PhotoError extends Error {}

function load(file: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  return img.decode().then(
    () => { URL.revokeObjectURL(url); return img; },
    () => { URL.revokeObjectURL(url); throw new PhotoError('That file isn’t a readable image.'); },
  );
}

const toBlob = (canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> => new Promise((r) => canvas.toBlob(r, 'image/jpeg', quality));

export async function preparePhoto(file: File, kind: PhotoKind): Promise<Blob> {
  if (!ACCEPTED.includes(file.type)) throw new PhotoError('Choose a JPG, PNG or WebP photo.');
  if (file.size > MAX_INPUT_BYTES) throw new PhotoError('That photo is too large (25 MB max).');
  const img = await load(file);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (w < 64 || h < 64) throw new PhotoError('That photo is too small.');

  let sx = 0, sy = 0, sw = w, sh = h;
  if (kind === 'avatar') {
    const side = Math.min(w, h);
    sx = Math.round((w - side) / 2);
    sy = Math.round((h - side) / 2);
    sw = sh = side;
  }
  const scale = Math.min(1, TARGET[kind] / Math.max(sw, sh));
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(16, Math.round(sw * scale));
  canvas.height = Math.max(16, Math.round(sh * scale));
  const g = canvas.getContext('2d');
  if (!g) throw new PhotoError('This browser can’t prepare photos.');
  g.fillStyle = '#FFFFFF'; // JPEG has no transparency
  g.fillRect(0, 0, canvas.width, canvas.height);
  g.imageSmoothingQuality = 'high';
  g.drawImage(img, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

  for (const q of [0.86, 0.75, 0.6]) {
    const blob = await toBlob(canvas, q);
    if (blob && blob.size <= MAX_OUTPUT_BYTES) return blob;
  }
  throw new PhotoError('That photo couldn’t be compressed enough.');
}
